export type Meridiem = 'AM' | 'PM';
export type TimeParts = { hour12: string; minute: string; meridiem: Meridiem };

const MINUTE_STEP = 5;

/**
 * Splits a 24-hour "HH:mm" value into the three parts the dropdowns show.
 * Returns null for anything unparseable so callers can fall back to an empty
 * control rather than rendering a half-set time.
 */
export function splitTimeValue(value: string | null | undefined): TimeParts | null {
	if (!value) return null;
	const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
	if (!match) return null;

	const hour24 = Number(match[1]);
	const minute = Number(match[2]);
	if (!Number.isInteger(hour24) || hour24 < 0 || hour24 > 23) return null;
	if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;

	// Midnight and noon are the two the modulo gets wrong on its own.
	const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
	return {
		hour12: String(hour12),
		minute: String(minute).padStart(2, '0'),
		meridiem: hour24 < 12 ? 'AM' : 'PM'
	};
}

export function joinTimeValue(parts: TimeParts): string {
	const hour12 = Number(parts.hour12);
	const minute = Number(parts.minute);
	if (!Number.isInteger(hour12) || hour12 < 1 || hour12 > 12) return '';
	if (!Number.isInteger(minute) || minute < 0 || minute > 59) return '';

	const base = hour12 % 12;
	const hour24 = parts.meridiem === 'PM' ? base + 12 : base;
	return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));

/**
 * Five-minute steps keep the list short enough to pick from, but an existing
 * session saved at an odd minute has to stay selectable or editing it would
 * silently move it.
 */
export function minuteOptions(current?: string | null): string[] {
	const steps: string[] = [];
	for (let m = 0; m < 60; m += MINUTE_STEP) steps.push(String(m).padStart(2, '0'));
	if (!current) return steps;

	const padded = String(Number(current)).padStart(2, '0');
	if (!/^\d{2}$/.test(padded) || Number(padded) > 59 || steps.includes(padded)) return steps;
	return [...steps, padded].sort((a, b) => Number(a) - Number(b));
}
